import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/Api';
import AuthContext from '../context/AuthContext';
import { FaUserCircle, FaCamera, FaTimes } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
  const { employeeNumber } = useContext(AuthContext);
  const [profileData, setProfileData] = useState(null);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/teachers/get-teacher-by-employeenumber/${employeeNumber}`);
        setProfileData(response.data);
        console.log(response.data);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    if (employeeNumber) {
      fetchProfile();
    }
  }, [employeeNumber]);

  const handleImageClick = (e) => {
    e.stopPropagation();
    setIsEditingImage(true);
  };

  const handleModalClose = (e) => {
    e.stopPropagation();
    setIsEditingImage(false);
  };

  const handleChooseImage = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPG or PNG)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5000000) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setNewImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateImage = async () => {
    try {
      const formData = new FormData();
      formData.append('image', newImage);

      const response = await axios.put(
        `${BASE_URL}/teachers/update-image/${employeeNumber}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.status === 200) {
        setProfileData(prev => ({
          ...prev,
          ImagePath: response.data.imagePath
        }));
        toast.success('Profile picture updated successfully');
        setIsEditingImage(false);
        setNewImage(null);
        setImagePreview(null);
      }
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Failed to update profile picture');
    }
  };

  const cancelImageUpdate = () => {
    setIsEditingImage(false);
    setNewImage(null);
    setImagePreview(null);
  };

  if (!profileData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#11024D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 md:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-[#11024D] h-32"></div>
          <div className="relative px-6 pb-6">
            <div className="absolute -top-16 left-6">
              <div className="relative">
                {/* Image Container */}
                <div className="relative group">
                  {imagePreview || profileData.ImagePath ? (
                    <img
                      src={imagePreview || `${BASE_URL}${profileData.ImagePath}`}
                      alt={`${profileData.Name} ${profileData.Surname}`}
                      className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                      <FaUserCircle className="w-20 h-20 text-gray-400" />
          </div>
                  )}
                  
                  {/* Edit Button */}
                  <button
                    type="button"
                    onClick={handleImageClick}
                    className="absolute bottom-0 right-0 bg-[#11024D] text-white p-2 rounded-full shadow-lg hover:bg-[#0A0033] transition-colors"
                    title="Edit profile picture"
                  >
                    <FaCamera className="text-lg" />
                  </button>
        </div>

                {/* Edit Image Modal */}
                {isEditingImage && (
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={handleModalClose}
                  >
                    <div 
                      className="bg-white rounded-lg shadow-xl p-6 w-96 mx-4"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">Update Profile Picture</h3>
                        <button
                          type="button"
                          onClick={handleModalClose}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <FaTimes />
                        </button>
      </div>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                        ref={fileInputRef}
                      />

                      {/* Preview of new image */}
                      {imagePreview && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-500 mb-2">Preview:</p>
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-lg"
                          />
                        </div>
                      )}

                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={handleChooseImage}
                          className="w-full bg-[#11024D] text-white px-4 py-3 rounded-lg hover:bg-[#0A0033] transition-colors flex items-center justify-center"
                        >
                          <FaCamera className="mr-2" />
                          {imagePreview ? 'Choose Different Picture' : 'Choose Picture'}
                        </button>

                        {newImage && (
                          <>
                            <button
                              type="button"
                              onClick={handleUpdateImage}
                              className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                            >
                              Save Changes
                            </button>
                            <button
                              type="button"
                              onClick={cancelImageUpdate}
                              className="w-full bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
          </div>
          </div>
                )}
          </div>
          </div>
            <div className="ml-40 pt-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {profileData.Name} {profileData.Surname}
              </h1>
              <p className="text-gray-600">Employee ID: {profileData.EmployeeNumber}</p>
              <p className="text-gray-600">{profileData.Role} - {profileData.Department}</p>
          </div>
          </div>
          </div>

        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-[#11024D] mb-6">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoCard label="Date of Birth" value={new Date(profileData.DateOfBirth).toLocaleDateString()} />
            <InfoCard label="National ID" value={profileData.NationalIDNumber} />
            <InfoCard label="Phone Number" value={profileData.PhoneNumber} />
            <InfoCard label="Gender" value={profileData.Gender} />
            <InfoCard label="Address" value={profileData.Address} />
            <InfoCard label="Department" value={profileData.Department} />
            <InfoCard label="Role" value={profileData.Role} />
            <InfoCard label="Employee Number" value={profileData.EmployeeNumber} />
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </div>
  );
};

// Info Card Component
const InfoCard = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className="text-lg font-semibold text-gray-800">{value}</p>
  </div>
);

export default Profile;
